import React, { useState } from 'react';
import { CheckCircle2, X, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { QuickStatOverrideDialog } from '@/components/live-scoring/QuickStatOverrideDialog';
import type { MatchStatsResponse } from '@/api/feed/getFeedMatchStats';

interface PlayerScoringOptionProps {
  questionId?: string;
  fixtureId?: string;
  listLayout?: boolean;
  questionPoints?: number;
  option: {
    option_id?: string;
    option_text_en: string;
    option_text_fr?: string;
    option_image_en?: string | null;
    option_image_fr?: string | null;
    third_image_en?: string | null;
    third_image_fr?: string | null;
    is_correct: boolean;
    stats?: Record<string, number | string>;
    points?: number;
    grading_data?: {
      playerId?: string | number;
    };
  };
  statTypeCode?: string | null;
  language?: 'en' | 'fr';
  manualScoring?: boolean;
  matchStats?: MatchStatsResponse;
  onStatChange?: (optionId: string, statValue: number) => void;
  onSave?: (optionId: string) => void;
  onCancel?: (optionId: string) => void;
  onOverrideSuccess?: () => void;
}

export function PlayerScoringOption({
  questionId,
  fixtureId: fixtureIdProp,
  option,
  listLayout = false,
  questionPoints = 0,
  statTypeCode,
  language = 'en',
  manualScoring = false,
  matchStats,
  onStatChange,
  onSave,
  onCancel,
  onOverrideSuccess,
}: PlayerScoringOptionProps) {
  const optionText =
    language === 'en' ? option.option_text_en : option.option_text_fr || option.option_text_en;
  const optionImage =
    language === 'en' ? option.option_image_en : option.option_image_fr || option.option_image_en;
  const thirdImage =
    language === 'en' ? option.third_image_en : option.third_image_fr || option.third_image_en;

  const isCorrect = option.is_correct;
  const [isEditing, setIsEditing] = useState(false);
  const [editingValue, setEditingValue] = useState('');
  const [isOverrideDialogOpen, setIsOverrideDialogOpen] = useState(false);

  // Extract playerId from grading_data
  const playerId = option.grading_data?.playerId?.toString();

  // Get fixtureId from matchStats or prop (prop takes precedence)
  const fixtureId = fixtureIdProp || matchStats?.match.id;

  // Extract stat value for display using the question's stat_type_code
  let statValue: number | null = null;
  if (
    statTypeCode &&
    option.stats &&
    typeof option.stats === 'object' &&
    !Array.isArray(option.stats)
  ) {
    const rawValue = option.stats[statTypeCode];
    if (typeof rawValue === 'number') {
      statValue = rawValue;
    } else if (typeof rawValue === 'string') {
      const parsed = parseFloat(rawValue);
      statValue = !isNaN(parsed) ? parsed : null;
    }
  }

  const displayValue = statValue !== null ? statValue.toString() : '0';
  // Selection questions award the question's flat point value to the winning
  // option only — never stat × points. (A player with 3 shots on a 1pt question
  // earns 1pt, not 3.) Prefer an explicit per-option points value if the API
  // provides one.
  const optionPoints = option.points ?? (isCorrect ? questionPoints : 0);

  const handleEditClick = () => {
    if (manualScoring) {
      // If we have questionId, fixtureId, playerId, and statTypeCode, use QuickStatOverrideDialog
      if (questionId && fixtureId && playerId && statTypeCode) {
        setIsOverrideDialogOpen(true);
      } else {
        // Fallback to inline editing if we don't have all required data
        setEditingValue(displayValue);
        setIsEditing(true);
      }
    }
  };

  const handleSave = () => {
    if (option.option_id && onStatChange) {
      const numValue = parseInt(editingValue, 10);
      if (!isNaN(numValue)) {
        onStatChange(option.option_id, numValue);
      }
    }
    if (option.option_id && onSave) {
      onSave(option.option_id);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (option.option_id && onCancel) {
      onCancel(option.option_id);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const statAndEditControls = (
    <div className="flex items-center gap-2">
      {isEditing ? (
        <>
          <Input
            type="number"
            value={editingValue}
            onChange={e => setEditingValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="h-8 w-16 text-center"
            autoFocus
          />
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={handleSave}
            className="h-7 px-2 text-xs bg-blue-600 hover:bg-blue-700"
          >
            Save
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            className="h-7 w-7 p-0 hover:bg-gray-100"
          >
            <X className="h-4 w-4" />
          </Button>
        </>
      ) : (
        <>
          <span
            className={`text-lg font-semibold ${isCorrect ? 'text-green-600' : 'text-gray-600'}`}
          >
            {displayValue}
          </span>
          {manualScoring && (
            <button
              type="button"
              onClick={handleEditClick}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              aria-label="Edit stat value"
            >
              <Pencil className="h-4 w-4 text-blue-400" />
            </button>
          )}
        </>
      )}
    </div>
  );

  // Quick Stat Override Dialog - rendered in BOTH layouts. It must live outside
  // the layout-specific JSX because list layout (>4 options) returns early; if
  // the dialog were only in the grid-layout return, clicking the pencil on a
  // list-layout option would open nothing.
  const overrideDialog = questionId && fixtureId && playerId && statTypeCode && (
    <QuickStatOverrideDialog
      open={isOverrideDialogOpen}
      onOpenChange={setIsOverrideDialogOpen}
      questionId={questionId}
      fixtureId={fixtureId}
      playerId={playerId}
      statType={statTypeCode}
      currentValue={statValue ?? undefined}
      onSuccess={() => {
        setIsOverrideDialogOpen(false);
        onOverrideSuccess?.();
      }}
    />
  );

  if (listLayout) {
    return (
      <div
        className={`flex items-center gap-4 w-full p-3 rounded-lg border transition-colors ${
          isCorrect ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
        }`}
      >
        {/* Checkmark - Left (placeholder when incorrect for alignment) */}
        <div className="w-6 flex-shrink-0 flex items-center justify-center">
          {isCorrect && <CheckCircle2 className="h-6 w-6 text-green-600" />}
        </div>

        {/* Team Logo */}
        {thirdImage && (
          <img src={thirdImage} alt="" className="h-10 w-10 object-contain flex-shrink-0 rounded" />
        )}

        {/* Player Image */}
        {optionImage && (
          <img
            src={optionImage}
            alt={optionText}
            className="h-10 w-10 object-contain rounded-full flex-shrink-0"
          />
        )}

        {/* Player Info */}
        <div className="flex-1 min-w-0">
          <span className={`text-sm font-medium ${isCorrect ? 'text-gray-900' : 'text-gray-700'}`}>
            {optionText}
          </span>
        </div>

        {/* Stat Value + Edit */}
        <div className="flex-shrink-0">{statAndEditControls}</div>

        {/* Points */}
        <span
          className={`text-sm font-semibold flex-shrink-0 ${
            isCorrect ? 'text-green-600' : 'text-gray-600'
          }`}
        >
          {optionPoints}pts
        </span>

        {/* Quick Stat Override Dialog */}
        {overrideDialog}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-2 flex-1">
      {/* Option Card */}
      <div
        className={`relative w-full p-6 rounded-lg border transition-colors flex flex-col items-center justify-center min-h-[140px] ${
          isCorrect ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
        }`}
      >
        {/* Checkmark Icon - Top Right */}
        {isCorrect && (
          <div className="absolute top-2 right-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
          </div>
        )}

        {/* Option Image */}
        {(optionImage || thirdImage) && (
          <div className="mb-3">
            {optionImage && (
              <img
                src={optionImage}
                alt={optionText}
                className="h-16 w-16 object-contain rounded-full"
              />
            )}
            {thirdImage && !optionImage && (
              <img src={thirdImage} alt="" className="h-16 w-16 object-contain rounded-full" />
            )}
          </div>
        )}

        {/* Option Text */}
        <div className="text-center">
          <span className={`text-sm font-medium ${isCorrect ? 'text-gray-900' : 'text-gray-700'}`}>
            {optionText}
          </span>
        </div>
      </div>

      {/* Stat Value with Edit Controls - Center aligned */}
      <div className="flex items-center justify-center gap-2 w-full">{statAndEditControls}</div>

      {/* Quick Stat Override Dialog */}
      {overrideDialog}
    </div>
  );
}
